import { useState, useEffect, useMemo } from 'react';
import { Heart, ShoppingCart, Star, Truck, RotateCcw, Plus, Minus, Check, AlertCircle } from 'lucide-react';
import useWishlist from '../../hooks/useWishlist';
import { fetchProducts } from '../../utils/api';
import ReviewSection from './ReviewSection';

const cur      = () => window.themeData?.currency ?? '₹';
const anviBase = () => (window.themeData?.apiUrl ?? '').replace(/\/$/, '');

function StarRow({ rating = 0, size = 'sm' }) {
  const cls = size === 'sm' ? 'w-4 h-4' : 'w-5 h-5';
  return (
    <div className="flex items-center gap-0.5 text-secondary">
      {[1,2,3,4,5].map(n => (
        <Star key={n} className={`${cls} ${n <= Math.round(rating) ? 'fill-current' : 'opacity-25'}`} />
      ))}
    </div>
  );
}

// ── Colour swatch ─────────────────────────────────────────────────────────────
function ColourSwatch({ name, hex, selected, available, onClick }) {
  return (
    <button
      onClick={onClick}
      disabled={!available}
      title={name}
      style={{ '--swatch-color': hex }}
      className={`relative w-12 h-12 rounded-full transition-all duration-150 flex-shrink-0
        ${selected   ? 'ring-2 ring-offset-2 ring-gray-800 scale-105'
        : available  ? 'ring-1 ring-transparent hover:ring-gray-300 hover:scale-105'
        : 'opacity-30 cursor-not-allowed'}
      `}
    >
      <span className="absolute inset-0 rounded-full" style={{ backgroundColor: hex }} />
      {!available && (
        <span className="absolute inset-0 rounded-full flex items-center justify-center">
          <span className="w-px h-full bg-gray-400 rotate-45 block" />
        </span>
      )}
    </button>
  );
}

// ── Size button ───────────────────────────────────────────────────────────────
function SizeBtn({ label, selected, available, onClick }) {
  return (
    <button
      onClick={onClick}
      disabled={!available}
      className={`min-w-[56px] px-4 py-3 rounded-lg border text-sm font-bold transition-all duration-150
        ${selected    ? 'bg-gray-900 text-white border-gray-900'
        : available   ? 'bg-white text-gray-800 border-gray-300 hover:border-gray-900'
        : 'bg-gray-50 text-gray-300 border-gray-100 cursor-not-allowed line-through'}
      `}
    >
      {label}
    </button>
  );
}

// ── Urgency stock bar ─────────────────────────────────────────────────────────
function UrgencyBar({ stockQty }) {
  if (stockQty === null || stockQty > 20) return null;
  // Fill represents how much has been "sold" — higher sold = more urgency
  const fillPct = Math.min(92, Math.max(20, 100 - (stockQty / 50) * 100));
  return (
    <div className="mb-5">
      <p className="text-sm font-bold text-gray-800 mb-2">
        Hurry! Only <span className="text-red-500 font-black">{stockQty}</span> units left in stock!
      </p>
      <div className="h-3 bg-gray-200 rounded-full overflow-hidden">
        <div
          className="h-full rounded-full transition-all duration-500"
          style={{
            width: `${fillPct}%`,
            background: 'linear-gradient(to right, #ef4444, #f97316, #eab308)',
          }}
        />
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function ProductDetailPage() {
  const [product,        setProduct]        = useState(null);
  const [variations,     setVariations]     = useState([]);
  const [colourMap,      setColourMap]      = useState({}); // name.toLowerCase() → hex
  const [selectedAttrs,  setSelectedAttrs]  = useState({});
  const [related,        setRelated]        = useState([]);
  const [qty,            setQty]            = useState(1);
  const [activeImg,      setActiveImg]      = useState(0);
  const [activeTab,      setActiveTab]      = useState('details');
  const [addedToCart,    setAddedToCart]    = useState(false);
  const [cartError,      setCartError]      = useState('');
  const { toggle: toggleWishlist, isWishlisted } = useWishlist();

  // ── Fetch product + variations (all via public custom endpoints) ───────────
  useEffect(() => {
    const el  = document.getElementById('anvi-product-detail');
    const pid = el ? parseInt(el.dataset.productId ?? '0', 10) : 0;
    if (!pid) return;

    // Use our public endpoint — no auth needed, works for guests too
    fetch(`${anviBase()}/little-anvi/v1/products/${pid}`)
      .then(r => r.json())
      .then(p => {
        if (p.error) return; // 404 / unpublished
        setProduct(p);

        if (p.type === 'variable') {
          fetch(`${anviBase()}/little-anvi/v1/products/${pid}/variations`)
            .then(r => r.json())
            .then(setVariations)
            .catch(() => {});

          fetch(`${anviBase()}/little-anvi/v1/colour-swatches`)
            .then(r => r.json())
            .then(swatches => {
              const map = {};
              swatches.forEach(s => { map[s.name.toLowerCase()] = s.color; });
              setColourMap(map);
            })
            .catch(() => {});
        }

        if (p.related_ids?.length) {
          fetchProducts({ include: p.related_ids.slice(0, 8).join(','), per_page: 8 })
            .then(setRelated)
            .catch(() => {});
        }
      })
      .catch(() => {});
  }, []);

  // ── Find matching variation from selected attrs ─────────────────────────────
  const activeVariation = useMemo(() => {
    if (!variations.length) return null;
    return variations.find(v =>
      v.attributes.every(a => {
        const chosen = selectedAttrs[a.name];
        return !chosen || a.option.toLowerCase() === chosen.toLowerCase();
      })
    ) ?? null;
  }, [selectedAttrs, variations]);

  // Check if a specific option is available (any instock variation has it)
  const isOptionAvailable = (attrName, option) => {
    if (!variations.length) return true;
    return variations.some(v => {
      const match = v.attributes.some(a =>
        a.name === attrName && a.option.toLowerCase() === option.toLowerCase()
      );
      return match && v.stock_status !== 'outofstock';
    });
  };

  function selectAttr(name, value) {
    setSelectedAttrs(prev => ({ ...prev, [name]: prev[name] === value ? '' : value }));
    setCartError('');
  }

  // Cap qty when variation stock is known
  useEffect(() => {
    if (activeVariation?.manage_stock && activeVariation?.stock_quantity != null) {
      setQty(q => Math.min(q, Math.max(1, activeVariation.stock_quantity)));
    }
  }, [activeVariation]);

  // ── Sync image to variation image ─────────────────────────────────────────
  useEffect(() => {
    if (activeVariation?.image) {
      const idx = (product?.images ?? []).findIndex(img => img.src === activeVariation.image);
      if (idx >= 0) setActiveImg(idx);
    }
  }, [activeVariation, product]);

  // ── Add to cart ─────────────────────────────────────────────────────────────
  function handleAddToCart() {
    if (!product) return;
    const td = window.themeData ?? {};

    if (product.type === 'variable') {
      const variationAttrs = (product.attributes ?? []).filter(a => a.variation);
      const unselected = variationAttrs.filter(a => !selectedAttrs[a.name]);
      if (unselected.length) {
        setCartError(`Please select: ${unselected.map(a => a.name).join(', ')}`);
        return;
      }
      if (!activeVariation) {
        setCartError('This combination is not available.');
        return;
      }
      if (activeVariation.stock_status === 'outofstock') {
        setCartError('This option is out of stock.');
        return;
      }
    }

    setCartError('');
    const cartId = product.type === 'variable' ? activeVariation.id : product.id;
    const base   = (td.apiUrl ?? '').replace(/\/$/, '');

    fetch(`${base}/wc/store/v1/cart/add-item`, {
      method:      'POST',
      credentials: 'same-origin',
      headers:     { 'Content-Type': 'application/json', 'Nonce': td.wcStoreNonce ?? '' },
      body:        JSON.stringify({ id: cartId, quantity: qty }),
    })
      .then(r => {
        if (!r.ok) throw new Error();
        return r.json();
      })
      .then(data => {
        setAddedToCart(true);
        setTimeout(() => setAddedToCart(false), 2500);
        window.dispatchEvent(new CustomEvent('anvi:cart-update', { detail: { count: data.items_count ?? 0 } }));
      })
      .catch(() => {
        if (product.permalink) window.location.href = product.permalink;
      });
  }

  // ── Loading ─────────────────────────────────────────────────────────────────
  if (!product) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-20 flex flex-col items-center justify-center min-h-[50vh]">
        <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mb-4" />
        <p className="text-textLight font-bold">Loading product…</p>
      </div>
    );
  }

  // ── Derived values ──────────────────────────────────────────────────────────
  const isVariable    = product.type === 'variable';
  const basePrice     = parseFloat(product.price ?? 0);
  const baseRegular   = parseFloat(product.regular_price ?? 0);
  const displayPrice  = activeVariation ? parseFloat(activeVariation.price ?? basePrice) : basePrice;
  const displayRegular = activeVariation ? parseFloat(activeVariation.regular_price ?? baseRegular) : baseRegular;
  const onSale        = displayRegular > displayPrice;
  const rating        = parseFloat(product.average_rating ?? 0);
  const ratingCount   = product.rating_count ?? 0;
  const images        = product.images ?? [];
  const wishlisted    = isWishlisted(product.id);
  const categories    = product.categories ?? [];

  // Variation attrs that need selectors
  const variationAttrs = isVariable
    ? (product.attributes ?? []).filter(a => a.variation)
    : [];

  const maxQty = activeVariation?.manage_stock && activeVariation?.stock_quantity != null
    ? activeVariation.stock_quantity
    : 999;
  const isOutOfStock = activeVariation
    ? activeVariation.stock_status === 'outofstock'
    : product.stock_status === 'outofstock';

  const tabs = [
    { id: 'details',  label: 'Details' },
    { id: 'reviews',  label: `Reviews (${ratingCount})` },
    { id: 'shipping', label: 'Shipping' },
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20 pt-8">

      {/* Breadcrumbs */}
      <nav className="flex text-sm text-gray-500 mb-8 flex-wrap gap-1">
        <a href={window.themeData?.siteUrl ?? '/'} className="hover:text-primary">Home</a>
        <span>/</span>
        <a href={`${window.themeData?.siteUrl ?? ''}/shop/`} className="hover:text-primary">Shop</a>
        {categories[0] && (<><span>/</span><span className="text-textDark font-semibold">{categories[0].name}</span></>)}
        <span>/</span>
        <span className="text-textDark font-semibold">{product.name}</span>
      </nav>

      {/* Main product section */}
      <div className="bg-white rounded-3xl shadow-sm border border-gray-100 p-6 md:p-10 mb-12 flex flex-col lg:flex-row gap-10">

        {/* ── Image gallery ── */}
        <div className="w-full lg:w-1/2 flex flex-col gap-4">
          <div className="relative bg-gray-50 rounded-2xl h-80 md:h-[450px] flex items-center justify-center p-8 overflow-hidden group">
            <div className="absolute top-4 left-4 z-10 flex gap-2">
              {product.featured && <span className="bg-accent1 text-white text-xs font-bold px-3 py-1.5 rounded-md shadow-sm">HOT</span>}
              {onSale && <span className="bg-secondary text-textDark text-xs font-bold px-3 py-1.5 rounded-md shadow-sm">-{Math.round((1 - displayPrice / displayRegular) * 100)}%</span>}
            </div>
            <button
              onClick={() => toggleWishlist(product.id)}
              className={`absolute top-4 right-4 z-10 w-10 h-10 rounded-full backdrop-blur-sm flex items-center justify-center transition-colors shadow-sm text-lg ${wishlisted ? 'bg-accent1 text-white' : 'bg-white/90 text-gray-400 hover:text-accent1'}`}
            >
              <Heart className={`w-5 h-5 ${wishlisted ? 'fill-current' : ''}`} />
            </button>
            {images[activeImg]
              ? <img src={images[activeImg].src} alt={images[activeImg].alt || product.name} className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-500" />
              : <span className="text-9xl">🎁</span>
            }
          </div>
          {images.length > 1 && (
            <div className="flex gap-3 overflow-x-auto pb-2">
              {images.map((img, i) => (
                <button key={img.id} onClick={() => setActiveImg(i)}
                  className={`w-20 h-20 md:w-24 md:h-24 flex-shrink-0 bg-gray-50 rounded-xl border-2 p-2 transition-colors ${i === activeImg ? 'border-primary' : 'border-gray-200 hover:border-primary'}`}>
                  <img src={img.src} alt={img.alt} className="w-full h-full object-contain" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* ── Product info ── */}
        <div className="w-full lg:w-1/2 flex flex-col gap-4">

          {/* Rating */}
          <div className="flex items-center gap-2">
            <StarRow rating={rating} size="lg" />
            {ratingCount > 0 && <span className="text-gray-400 text-sm">({ratingCount} Reviews)</span>}
          </div>

          {/* Name */}
          <h1 className="text-3xl md:text-4xl font-black text-textDark leading-tight">{product.name}</h1>

          {/* Price */}
          <div className="flex items-baseline gap-3">
            <span className="text-4xl font-black text-textDark">{cur()}{displayPrice.toFixed(2)}</span>
            {onSale && <span className="text-xl text-gray-400 line-through">{cur()}{displayRegular.toFixed(2)}</span>}
          </div>

          {/* Short description */}
          {product.short_description && (
            <div className="text-gray-500 text-sm leading-relaxed" dangerouslySetInnerHTML={{ __html: product.short_description }} />
          )}

          {/* Wishlist row */}
          <div className="flex items-center gap-5 text-sm text-gray-500">
            <button onClick={() => toggleWishlist(product.id)} className={`flex items-center gap-1.5 font-semibold hover:text-accent1 transition-colors ${wishlisted ? 'text-accent1' : ''}`}>
              <Heart className={`w-4 h-4 ${wishlisted ? 'fill-current text-accent1' : ''}`} />
              {wishlisted ? 'Wishlisted' : 'Add To Wishlist'}
            </button>
          </div>

          {/* Urgency stock bar */}
          {(() => {
            const sqty = activeVariation?.manage_stock ? activeVariation.stock_quantity
                       : (product.manage_stock ? product.stock_quantity : null);
            return <UrgencyBar stockQty={sqty} />;
          })()}

          {/* ── Variation selectors ── */}
          {isVariable && variationAttrs.length > 0 && (
            <div className="flex flex-col gap-5">
              {variationAttrs.map(attr => {
                const isColour = attr.name.toLowerCase().includes('colour') || attr.name.toLowerCase().includes('color');
                const selected = selectedAttrs[attr.name] ?? '';
                return (
                  <div key={attr.id}>
                    <p className="text-sm font-bold text-gray-700 mb-2.5">{attr.name}</p>
                    {isColour ? (
                      <div className="flex flex-wrap gap-3">
                        {attr.options.map(opt => (
                          <ColourSwatch
                            key={opt}
                            name={opt}
                            hex={colourMap[opt.toLowerCase()] ?? '#cccccc'}
                            selected={selected === opt}
                            available={isOptionAvailable(attr.name, opt)}
                            onClick={() => selectAttr(attr.name, opt)}
                          />
                        ))}
                      </div>
                    ) : (
                      <div className="flex flex-wrap gap-2">
                        {attr.options.map(opt => (
                          <SizeBtn
                            key={opt}
                            label={opt}
                            selected={selected === opt}
                            available={isOptionAvailable(attr.name, opt)}
                            onClick={() => selectAttr(attr.name, opt)}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Cart error */}
          {cartError && (
            <div className="flex items-center gap-2 text-red-500 text-sm font-semibold bg-red-50 px-3 py-2 rounded-xl">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              {cartError}
            </div>
          )}

          {/* Quantity + Add to cart */}
          <div className="flex flex-col gap-3 pt-2">
            <p className="text-sm font-bold text-gray-700">Quantity</p>
            <div className="flex gap-3 flex-wrap">
              {/* Stepper */}
              <div className="flex items-center bg-gray-100 rounded-2xl p-1 gap-1">
                <button onClick={() => setQty(q => Math.max(1, q - 1))}
                  className="w-11 h-11 rounded-xl flex items-center justify-center text-gray-600 hover:bg-white hover:shadow-sm transition-all">
                  <Minus className="w-4 h-4" />
                </button>
                <span className="font-bold text-lg text-textDark w-10 text-center">{qty}</span>
                <button onClick={() => setQty(q => Math.min(maxQty, q + 1))} disabled={qty >= maxQty}
                  className="w-11 h-11 rounded-xl flex items-center justify-center text-gray-600 hover:bg-white hover:shadow-sm transition-all disabled:opacity-30">
                  <Plus className="w-4 h-4" />
                </button>
              </div>
              {/* Add to cart */}
              <button onClick={handleAddToCart} disabled={isOutOfStock}
                className={`flex-1 min-w-[160px] font-black text-base py-3 px-6 rounded-2xl transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed
                  ${addedToCart ? 'bg-accent3 text-white' : 'bg-primary hover:bg-primaryHover text-white'}`}>
                {addedToCart ? <Check className="w-5 h-5" /> : <ShoppingCart className="w-5 h-5" />}
                {isOutOfStock ? 'Out of Stock' : addedToCart ? 'Added!' : 'Add to Cart'}
              </button>
            </div>
          </div>

          {/* Delivery info */}
          <div className="flex flex-col gap-3 text-sm text-gray-500">
            <div className="flex items-center gap-2">
              <Truck className="w-4 h-4 text-primary flex-shrink-0" />
              <span>Standard delivery ₹70 &nbsp;·&nbsp; <span className="text-accent3 font-bold">Free on orders above ₹1999</span></span>
            </div>
            <div className="hidden flex items-center gap-2">
              <RotateCcw className="w-4 h-4 text-primary flex-shrink-0" />
              <span>Free 30-day returns</span>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-3xl shadow-sm border border-gray-100 overflow-hidden mb-12">
        <div className="flex border-b border-gray-100">
          {tabs.map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              className={`flex-1 py-5 font-bold text-lg transition-colors ${activeTab === tab.id ? 'text-primary border-b-2 border-primary bg-primary/5' : 'text-gray-500 hover:text-primary hover:bg-gray-50'} ${tab.id === 'shipping' ? 'hidden sm:block' : ''}`}>
              {tab.label}
            </button>
          ))}
        </div>
        <div className="p-8 md:p-10">
          {activeTab === 'details' && (
            <div>
              <h3 className="text-xl font-bold text-textDark mb-4">Product Description</h3>
              {product.description
                ? <div className="text-gray-600 leading-relaxed prose max-w-none" dangerouslySetInnerHTML={{ __html: product.description }} />
                : <p className="text-gray-500">No detailed description available.</p>
              }
            </div>
          )}
          {activeTab === 'reviews' && (
            <ReviewSection productId={product.id} />
          )}
          {activeTab === 'shipping' && (
            <div className="space-y-4 text-gray-600">
              {[
                'Standard delivery ₹70. Delivery in 3–5 business days.',
                'Free shipping on orders above ₹1999 — automatically applied at checkout.',
                'Free 30-day returns. Return in original packaging for a full refund.',
              ].map((text, i) => (
                <div key={i} className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-accent3/20 text-accent3 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Check className="w-3 h-3" />
                  </div>
                  <p dangerouslySetInnerHTML={{ __html: text }} />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Related products */}
      {related.length > 0 && (
        <div>
          <h2 className="text-2xl md:text-3xl font-black text-textDark mb-8">You May Also Like</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {related.slice(0, 4).map(p => {
              const pPrice = parseFloat(p.price ?? 0);
              const pImg   = p.images?.[0]?.src;
              return (
                <a key={p.id} href={p.permalink ?? '#'} className="bg-white rounded-3xl border border-gray-100 shadow-sm hover:shadow-xl transition-all duration-300 overflow-hidden group flex flex-col">
                  <div className="h-48 bg-gray-50 p-6 flex items-center justify-center group-hover:bg-gray-100 transition-colors overflow-hidden">
                    {pImg ? <img src={pImg} alt={p.name} className="h-full w-full object-contain group-hover:scale-110 transition-transform duration-500" /> : <span className="text-6xl">🎁</span>}
                  </div>
                  <div className="p-5 flex flex-col flex-grow">
                    <StarRow rating={parseFloat(p.average_rating ?? 0)} />
                    <h3 className="font-bold text-textDark text-base mt-2 mb-1 line-clamp-2 hover:text-primary transition-colors">{p.name}</h3>
                    <div className="mt-auto flex items-center justify-between pt-4">
                      <span className="font-black text-xl text-primary">{cur()}{pPrice.toFixed(2)}</span>
                    </div>
                  </div>
                </a>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
